import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, ViewChild } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { catchError, Observable, throwError } from 'rxjs';
import { TabViewCloseEvent, TabViewModule } from 'primeng/tabview';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ContextMenu, ContextMenuModule } from 'primeng/contextmenu';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { LucideAngularModule, CirclePlusIcon } from 'lucide-angular';
import { BaseFormComponent } from '@src/app/components';
import { BindingSource, ParameterType, ReportDto, ReportParameter, ReportsService } from '@src/api/reports';
import { fixNullToUndefined, fixUndefinedToNull } from '@src/app/utils';

import {
  CommonFormComponent,
  createCommonForm,
  createDatasourceForm,
  createFilterForm,
  createParamForm,
  DatasourceFormComponent,
  DatasourceFormDataType,
  FilterFormComponent,
  FilterFormDataType,
  FilterFormType,
  AccessSettingsFormComponent,
  createAccessSettingsForm,
} from './components';
import { FormDataType, FormType } from './types';
import { REPORT_SOURCE_TYPES } from './constants';
import { FilterTransferService } from './services';

@Component({
  selector: 'app-report-designer',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CommonFormComponent,
    DatasourceFormComponent,
    FilterFormComponent,
    TabViewModule,
    ButtonModule,
    TooltipModule,
    LucideAngularModule,
    AccessSettingsFormComponent,
    ContextMenuModule,
  ],
  templateUrl: './report-designer.component.html',
  styleUrl: './report-designer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportDesignerComponent extends BaseFormComponent<FormDataType, ReportDto> {
  private readonly reportsService = inject(ReportsService);
  private readonly filterTransferService = inject(FilterTransferService);
  private readonly confirmationService = inject(ConfirmationService);

  readonly CirclePlusIcon = CirclePlusIcon;
  readonly reportSourceTypes = REPORT_SOURCE_TYPES;

  formName = 'report-designer';
  moduleTypes$ = undefined;

  filtersActiveIndex = 0;

  @ViewChild(ContextMenu) contextMenu?: ContextMenu;
  contextMenuItems: MenuItem[] = [];
  selectFilterControl?: FormGroup<FilterFormType>;
  private readonly copyContextMenuItems = [
    {
      label: 'Копировать фильтр',
      command: () => {
        if (this.selectFilterControl) {
          this.filterTransferService.copyData(this.selectFilterControl.getRawValue());
        }
      },
    },
  ];
  private readonly pasteContextMenuItems = [
    {
      label: 'Вставить фильтр',
      command: () => {
        const data = this.filterTransferService.getData();
        if (data) {
          this.addFilterControl(data);
        }
      },
    },
  ];

  get commonControl() {
    return this.form.controls.common as FormType['common'];
  }
  get datasourceControl() {
    return this.form.controls.datasource as FormType['datasource'];
  }
  get filtersControl() {
    return this.form.controls.filters as FormType['filters'];
  }
  get accessSettingsControl() {
    return this.form.controls.accessSettings as FormType['accessSettings'];
  }

  constructor() {
    super(
      new FormGroup<FormType>({
        common: createCommonForm(),
        datasource: createDatasourceForm({ type: REPORT_SOURCE_TYPES[0] }),
        filters: new FormArray<FormGroup>([]),
        accessSettings: createAccessSettingsForm(),
      }),
    );
  }

  override getData(uuid: string): Observable<ReportDto> {
    return this.reportsService.getReportByUuid(uuid).pipe(
      catchError(err => {
        this.alertService.error(err.error.message, true);
        return throwError(() => new Error(err));
      }),
    );
  }

  override onCreate(data: ReportDto) {
    return this.reportsService.createReport(data);
  }

  override onUpdate(data: ReportDto) {
    if (!data.uuid) {
      throw new Error('показать ошибку');
    }

    return this.reportsService.updateReportByUuid(data.uuid, data);
  }

  addFilterControl(data?: FilterFormDataType) {
    const filterForm = createFilterForm(this.filtersControl.length + 1);
    if (data) {
      const paramsControl = filterForm.controls.params;
      data.params.forEach(() => {
        paramsControl.push(createParamForm());
      });

      filterForm.patchValue(data);
    }

    this.filtersControl.push(filterForm);

    this.moveTab(this.filtersControl.controls.length);
  }

  private removeFilterControl(index: number) {
    this.filtersControl.removeAt(index);

    this.moveTab(index);
  }

  getOtherFilters(currentFilterIndex: number) {
    return this.filtersControl
      .getRawValue()
      .filter((_, index) => index !== currentFilterIndex)
      .map(filter => filter.ui!);
  }

  closeTab(event: TabViewCloseEvent) {
    this.confirmationService.confirm({
      message: 'Вы действительно хотите удалить эту вкладку?',
      header: 'Подтверждение удаления',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        this.removeFilterControl(event.index);
        event.close?.();
      },
    });
  }

  private moveTab(index: number) {
    setTimeout(() => {
      this.filtersActiveIndex = index > 0 ? index - 1 : 0;
      this.cdr.markForCheck();
    }, 300);
  }

  override mapApiDataToFormData(data: ReportDto): FormDataType {
    const { uuid, name, description, formats, isEnabled, categories, template, connection, parameters, grantedUsers } =
      data;

    const formData: FormDataType = {
      common: {
        uuid: fixUndefinedToNull(uuid),
        name,
        description: fixUndefinedToNull(description),
        formats,
        isEnabled: isEnabled ?? false,
        categories: categories ?? [],
        template: template,
      },
      datasource: connection ? this.mapApiBindingSourceToFormDatasource(connection) : ({} as any), // TODO: исправить тип
      filters: parameters ? this.mapApiParameterToFormFilter(parameters) : [],
      accessSettings: {
        isPublic: !grantedUsers?.length,
        grantedUsers: !!grantedUsers?.length ? grantedUsers : [],
      },
    };

    return formData;
  }

  override mapFormDataToApiData(data: FormDataType): ReportDto {
    const { common, datasource, filters, accessSettings } = data;
    const { uuid, name, description, formats, template, isEnabled, categories } = common;
    const { grantedUsers } = accessSettings;

    const apiData: ReportDto = {
      uuid: fixNullToUndefined(uuid),
      id: name,
      name,
      description: fixNullToUndefined(description),
      formats,
      isEnabled,
      template: template ?? { uuid: '' }, // TODO: поправить
      categories,

      connection: this.mapFormDatasourceToApiBindingSource(datasource),
      parameters: this.mapFormFilterToApiParameter(filters),

      grantedUsers: !grantedUsers ? [] : grantedUsers,
    };

    return apiData;
  }

  private mapApiBindingSourceToFormDatasource(
    apiBindingSource: BindingSource,
    defaultValue?: any,
  ): DatasourceFormDataType {
    const { type, driver, username, password, query, uri, method, headers, body } = apiBindingSource;

    const formDatasource: DatasourceFormDataType = {
      type,
      // data: data, // TODO: нет на фронте
      driver: fixUndefinedToNull(driver),
      username: fixUndefinedToNull(username),
      password: fixUndefinedToNull(password),
      query: fixUndefinedToNull(query),
      uri: fixUndefinedToNull(uri),
      method: fixUndefinedToNull(method),
      headers: this.mapApiHeadersToFormHeaders(headers),
      body: this.mapApiHeadersToFormHeaders(body),
      defaultValue,
    };

    return formDatasource;
  }

  private mapFormDatasourceToApiBindingSource(formDatasource: DatasourceFormDataType): BindingSource {
    const { type, driver, username, password, query, uri, method, headers, body } = formDatasource;

    const apiBindingSource: BindingSource = {
      type,
      // data: data, // TODO: нет на фронте
      driver: fixNullToUndefined(driver),
      username: fixNullToUndefined(username),
      password: fixNullToUndefined(password),
      query: fixNullToUndefined(query),
      uri: fixNullToUndefined(uri),
      method: fixNullToUndefined(method),
      headers: this.mapFormHeadersToApiHeaders(headers),
      body: this.mapFormHeadersToApiHeaders(body),
    };

    return apiBindingSource;
  }

  private mapApiHeadersToFormHeaders(headers?: Record<string, string> | object): FormDataType['datasource']['headers'] {
    const formHeaders: FormDataType['datasource']['headers'] = {
      items: headers
        ? Object.entries(headers).map(([key, value]) => {
            return {
              key,
              value,
            };
          })
        : [],
    };

    return formHeaders;
  }

  private mapFormHeadersToApiHeaders(headers: FormDataType['datasource']['headers']): Record<string, string> {
    return headers.items.reduce(
      (acc, item) => {
        const { key, value } = item;
        if (key && value) {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, string>,
    );
  }

  private mapApiParameterToFormFilter(parameters: ReportParameter[]): FilterFormDataType[] {
    const filters: FilterFormDataType[] = [];
    parameters.forEach(parameter => {
      const filterControl = createFilterForm(this.filtersControl.length + 1);
      this.filtersControl.push(filterControl);
      const paramsControl = filterControl.controls.params;

      const { id, ui, binding, submissions } = parameter;
      const { type, label, placeholder, order, visible } = ui;
      const { dependsOn, defaultValue, provider, source, mappings } = binding;

      filters.push({
        ui: {
          id,
          type,
          label,
          placeholder: fixUndefinedToNull(placeholder),
          order,
          visible,
        },
        datasource: {
          dependsOn: dependsOn ?? [],
          front: this.mapApiBindingSourceToFormDatasource(provider, defaultValue),
          back: source ? this.mapApiBindingSourceToFormDatasource(source) : ({} as any), // TODO: исправить тип
        },
        mapping: {
          value: mappings?.value ?? '',
          display: mappings?.display ?? '',
        },
        params: this.mapApiSubmissionsToFormParams(submissions, paramsControl),
      });
    });

    return filters;
  }

  private mapFormFilterToApiParameter(filters: FilterFormDataType[]): ReportParameter[] {
    const parameters: ReportParameter[] = [];
    filters.forEach(filter => {
      const { ui, datasource, mapping, params } = filter;
      const { id, label, placeholder, order, visible } = ui;
      const { dependsOn, front, back } = datasource;
      const { value, display } = mapping;

      parameters.push({
        id,
        ui: {
          type: ui.type,
          label,
          placeholder: fixNullToUndefined(placeholder),
          order,
          visible,
        },
        binding: {
          dependsOn,
          defaultValue: front.defaultValue,
          provider: this.mapFormDatasourceToApiBindingSource(front),
          source: this.mapFormDatasourceToApiBindingSource(back),
          mappings: {
            value,
            display,
          },
        },
        submissions: this.mapFormParamsToApiSubmissions(params, ui.type),
      });
    });

    return parameters;
  }

  private mapApiSubmissionsToFormParams(
    submissions: ReportParameter['submissions'],
    paramsControl: FilterFormType['params'],
  ): FilterFormDataType['params'] {
    const params: FilterFormDataType['params'] = [];
    submissions.forEach(submission => {
      paramsControl.push(createParamForm());

      const { parameter, source, format } = submission;

      params.push({
        parameter,
        format: fixUndefinedToNull(format),
        source,
        // required?: boolean; // TODO: нет на фронте
      });
    });

    return params;
  }

  private mapFormParamsToApiSubmissions(
    params: FilterFormDataType['params'],
    type: ParameterType,
  ): ReportParameter['submissions'] {
    const submissions: ReportParameter['submissions'] = [];
    params.forEach(param => {
      const { parameter, source, format } = param;

      submissions.push({
        parameter,
        type,
        format: fixNullToUndefined(format),
        source,
        // required?: boolean; // TODO: нет на фронте
      });
    });

    return submissions;
  }

  onCopyContextMenu(event: MouseEvent, filterControl?: FormGroup<FilterFormType>) {
    this.selectFilterControl = filterControl;
    this.contextMenuItems = this.copyContextMenuItems;
    this.contextMenu?.show(event);
  }

  onPasteContextMenu(event: MouseEvent) {
    this.contextMenuItems = this.pasteContextMenuItems;
    if (!!this.filterTransferService.getData()) {
      this.contextMenu?.show(event);
    }
  }
}
